import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    IconLayoutDashboard,
    IconShoppingBag,
    IconMapPin,
    IconUser,
    IconLanguage,
    IconCurrencyDollar,
    IconAddressBook,
    IconArrowBackUp,
    IconTruckReturn,
    IconCoins,
    IconShare,
    IconTag,
    IconWallet,
    IconId,
    IconLock,
    IconBell,
    IconX,
    IconChevronDown,
    IconHome,
    IconHeart
} from '@tabler/icons-react';
import { Avatar, Typography, Divider } from 'antd';
import AuthServ from "../../service/AuthServ";
import { getImageUrl } from "../SafeImage";
import near from "../../assets/img/near.jpg";
import "../../css/sideNav.css";
import { usePreference } from '../../context/PreferenceContext';

const { Text } = Typography;

const BuyerSideNav = ({ isOpen, onClose }) => {
    const { t } = usePreference();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeKey, setActiveKey] = useState("");
    const [openKeys, setOpenKeys] = useState({});
    const [customerName, setCustomerName] = useState("");
    const [role, setRole] = useState(localStorage.getItem("role") || "");
    const [user, setUser] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("token") || localStorage.getItem("customerToken");
        if (token) {
            AuthServ.single().then(res => {
                if (res?.data) {
                    const userObj = res.data;
                    setUser(userObj);
                    setRole(userObj.role?.toLowerCase());
                    let displayName = userObj.firstName
                        ? `${userObj.firstName} ${userObj.lastName || ""}`.trim()
                        : userObj.name || userObj.email?.split('@')[0];
                    setCustomerName(displayName || t('nearzon_user'));
                }
            }).catch(err => console.error("Profile sync failed", err));
        }
    }, []);

    const handleProfileNavigate = () => {
        navigate("/buyer/profile");
        onClose();
    };

    const items = [
        {
            key: '/buyer/dashboard',
            icon: <IconLayoutDashboard size={20} />,
            label: t('dashboard'),
            path: '/buyer/dashboard',
        },
        {
            key: 'orders',
            icon: <IconShoppingBag size={20} />,
            label: t('orders'),
            children: [
                { key: '/buyer/orders', icon: <IconShoppingBag size={18} />, label: t('my_orders'), path: '/buyer/orders' },
                { key: '/buyer/return-requests', icon: <IconTruckReturn size={18} />, label: t('return_requests'), path: '/buyer/return-requests' },
                { key: '/buyer/cancellation-requests', icon: <IconArrowBackUp size={18} />, label: t('cancellations_and_refunds', 'Cancellations & Refunds'), path: '/buyer/cancellation-requests' },
                { key: '/buyer/payments/history', icon: <IconId size={18} />, label: t('payment_history', 'Payment History'), path: '/buyer/payments/history' },
            ],
        },
        { key: '/buyer/profile', icon: <IconId size={20} />, label: t('personal_info'), path: '/buyer/profile' },
        { key: '/buyer/wishlist', icon: <IconHeart size={20} />, label: t('wishlist', 'My Wishlist'), path: '/buyer/wishlist' },
        // { key: '/buyer/addresses', icon: <IconMapPin size={20} />, label: t('my_addresses'), path: '/buyer/addresses' },
        // { key: '/buyer/security', icon: <IconLock size={20} />, label: t('security'), path: '/buyer/security' },
        { key: '/buyer/rewards/points', icon: <IconCoins size={20} />, label: t('reward_points'), path: '/buyer/rewards/points' },
        { key: '/buyer/rewards/share', icon: <IconShare size={20} />, label: t('share_earn'), path: '/buyer/rewards/share' },
        { key: '/buyer/rewards/offers', icon: <IconTag size={20} />, label: t('my_offers'), path: '/buyer/rewards/offers' },
        { key: '/buyer/rewards/credits', icon: <IconWallet size={20} />, label: t('my_credits'), path: '/buyer/rewards/credits' },
        {
            key: '/buyer/contact',
            icon: <IconAddressBook size={20} />,
            label: t('contact_us'),
            path: '/buyer/contact',
        },
    ];

    useEffect(() => {
        const findKeyByPath = (items, path) => {
            for (let item of items) {
                if (item.path === path) return item.key;
                if (item.children) {
                    const child = findKeyByPath(item.children, path);
                    if (child) return child;
                }
            }
            return null;
        };
        const currentKey = findKeyByPath(items, location.pathname);
        if (currentKey) {
            setActiveKey(currentKey);
            items.forEach(item => {
                if (item.children && item.children.find(c => c.key === currentKey)) {
                    setOpenKeys(prev => ({ ...prev, [item.key]: true }));
                }
            });
        }
    }, [location.pathname]);

    const handleNavigation = (key, path) => {
        setActiveKey(key);
        if (path) navigate(path);
        if (window.innerWidth <= 768) onClose();
    };

    const toggleParent = (key) => {
        setOpenKeys((prev) => {
            if (prev[key]) return { ...prev, [key]: false };
            return { [key]: true }; // Accordion style
        });
    };

    const renderNavItems = () => {
        if (!isMobile) {
            return items.map((item) => (
                <div key={item.key}>
                    <div
                        className={`nav-item parent-item ${activeKey === item.key ? "active" : ""} ${openKeys[item.key] ? "open" : ""}`}
                        onClick={() =>
                            item.children
                                ? toggleParent(item.key)
                                : handleNavigation(item.key, item.path)
                        }
                    >
                        {item.icon}
                        <span className="menu-label-text">{item.label}</span>
                        {item.children && (
                            <IconChevronDown className="expansion-icon" size={16} stroke={2} />
                        )}
                    </div>

                    {item.children && (
                        <div
                            className="submenu"
                            style={{
                                maxHeight: openKeys[item.key] ? "1000px" : "0",
                                opacity: openKeys[item.key] ? 1 : 0
                            }}
                        >
                            {item.children.map((child) => (
                                <div
                                    key={child.key}
                                    className={`nav-item submenu-item ${activeKey === child.key ? "active" : ""}`}
                                    onClick={() => handleNavigation(child.key, child.path)}
                                >
                                    {child.icon}
                                    <span className="menu-label-text">{child.label}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ));
        }

        return (
            <div className="mobile-menu-body">
                <div className="menu-group">
                    <Text className="menu-group-label">{t('navigation')}</Text>
                    <div className="nav-item" onClick={() => { navigate("/"); onClose(); }}>
                        <IconHome size={20} /> <span className="menu-label-text">{t('home')}</span>
                    </div>
                    {items.map(item => (
                        <div key={item.key}>
                            <div
                                className={`nav-item parent-item ${activeKey === item.key ? "active" : ""} ${openKeys[item.key] ? "open" : ""}`}
                                onClick={() =>
                                    item.children
                                        ? toggleParent(item.key)
                                        : handleNavigation(item.key, item.path)
                                }
                            >
                                {item.icon}
                                <span className="menu-label-text">{item.label}</span>
                                {item.children && (
                                    <IconChevronDown className="expansion-icon" size={16} stroke={2} />
                                )}
                            </div>

                            {item.children && (
                                <div
                                    className="submenu"
                                    style={{
                                        maxHeight: openKeys[item.key] ? "1000px" : "0",
                                        opacity: openKeys[item.key] ? 1 : 0
                                    }}
                                >
                                    {item.children.map((child) => (
                                        <div
                                            key={child.key}
                                            className={`nav-item submenu-item ${activeKey === child.key ? "active" : ""}`}
                                            onClick={() => handleNavigation(child.key, child.path)}
                                        >
                                            {child.icon}
                                            <span className="menu-label-text">{child.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="custom-sidenav">
            <div className={`sidenav ${isOpen ? "open" : ""}`}>
                {isMobile ? (
                    <div className="mobile-menu-wrapper">
                        <div className="mobile-menu-header">
                            <div className="pcu-flex pcu-justify-between pcu-flex-center-y pcu-margin-bottom-20">
                                <img src={near} alt="logo" className="pcu-h-40" onClick={() => { navigate("/"); onClose(); }} />
                                <button onClick={onClose} className="close-btn">
                                    <IconX size={20} />
                                </button>
                            </div>
                            <div className="mobile-user-profile" onClick={handleProfileNavigate}>
                                <Avatar size={60} icon={<IconUser />} src={getImageUrl(user?.profilePhoto)} className="mobile-avatar" />
                                <div className="mobile-user-text">
                                    <Text strong className="mobile-user-name">{customerName || t('nearzon_user')}</Text>
                                    <div className="pcu-flex-center-y pcu-gap-8">
                                        <Text type="secondary" className="mobile-user-role">{t(role?.toLowerCase())?.toUpperCase() || t('buyer')?.toUpperCase()}</Text>
                                        <div className="pcu-w-4 pcu-h-4 pcu-radius-50 pcu-bg-muted"></div>
                                        <Text type="secondary" className="pcu-font-12 pcu-color-orange pcu-font-600">{t('edit_profile')}</Text>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Divider className="pcu-margin-0" />
                        <div className="pcu-padding-y-10">
                            {renderNavItems()}
                        </div>
                    </div>
                ) : (
                    <>
                        <nav>
                            {renderNavItems()}
                        </nav>
                    </>
                )}
            </div>
        </div>
    );
};

export default BuyerSideNav;
